import React from "react";
import styled from "styled-components";
import { router, Link } from "@inertiajs/react";
import { UtensilsCrossed, BedDouble } from "lucide-react";
import { PageHeader } from "../../../../components/page-header";
import { useLanguage } from "../../../../contexts/language";
import { useNotification } from "../../../../contexts/notification";
import {
  saveOrderToLocalHistory,
  getOrderKindFromLocal,
  orderApiPath,
} from "../../../../util";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 45%, #f4f4f4 100%);
  padding: 20px 16px 28px;
`;

const Wrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 20px 20px 18px;
  margin-bottom: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0 0 8px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const StatusLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.$color || "#5f5f5f"};
  background: ${(props) => props.$background || "#f1f1f1"};
`;

const TotalRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ececec;
  display: flex;
  align-items: end;
  justify-content: space-between;
`;

const TotalLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #c9a961;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const TimeValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

const ActionRow = styled.div`
  margin-top: 14px;
`;

const Button = styled.a`
  display: inline-block;
  padding: 11px 16px;
  border-radius: 10px;
  background: #c9a961;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #b69352;
    box-shadow: 0 6px 16px rgba(201, 169, 97, 0.35);
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 28px;
  font-weight: 600;
  color: #1f1f1f;
`;

const InvoiceBox = styled.div`
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid #ecdfba;
  border-radius: 10px;
  background: #fbf6e9;
`;

const InvoiceLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #927a3a;
  margin-bottom: 2px;
`;

const InvoiceValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #5f4e1d;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
`;

const BodySectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #222;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListItem = styled.li`
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fcfcfc;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const ItemHeadline = styled.div`
  font-size: 15px;
  color: #2b2b2b;
  font-weight: 500;
`;

const ItemNote = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #767676;
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
`;

const OrderMoreRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const OrderMoreLink = styled(Link)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 10px;
  border: 1px dashed #c9a961;
  border-radius: 12px;
  background: rgba(201, 169, 97, 0.04);
  color: #c9a961;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(201, 169, 97, 0.1);
    box-shadow: 0 2px 8px rgba(201, 169, 97, 0.15);
  }
`;

export default function OrderShowPage(props) {
  const { building_code, order_id } = props;
  const { t } = useLanguage();
  const { subscribe } = useNotification();
  const [order, setOrder] = React.useState(null);
  const [hasAutoOpenedPayment, setHasAutoOpenedPayment] = React.useState(false);

  const loadOrder = React.useCallback(async () => {
    const kind = getOrderKindFromLocal(building_code, order_id);
    const response = await fetch(orderApiPath({ id: order_id, kind }), {
      headers: { Accept: "application/json" },
    });
    const payload = await response.json();
    setOrder(payload?.data || null);
  }, [order_id, building_code]);

  React.useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 10000);
    return () => clearInterval(interval);
  }, [loadOrder]);

  React.useEffect(() => {
    if (!order?.id) return;
    saveOrderToLocalHistory({ buildingCode: building_code, order });
  }, [order, building_code]);

  React.useEffect(() => {
    if (hasAutoOpenedPayment || !order?.payment_link_url) return;

    const queryParams = new URLSearchParams(window.location.search);
    const shouldAutoPay = queryParams.get("auto_pay") === "1";
    if (!shouldAutoPay) return;

    const continueToPayment = async () => {
      setHasAutoOpenedPayment(true);
      await subscribe();
      window.location.assign(order.payment_link_url);
    };

    continueToPayment();
  }, [order, hasAutoOpenedPayment, subscribe]);

  const renderStatusBadge = (type, value) => {
    const normalized = String(value || "-").toLowerCase();

    if (type === "payment") {
      if (normalized === "paid") {
        return (
          <StatusBadge $color="#0f7a39" $background="#dff7e8">
            {t.orderDetail.paid}
          </StatusBadge>
        );
      }
      if (normalized === "pending") {
        return (
          <StatusBadge $color="#9a6a00" $background="#fff4da">
            {t.orderDetail.pending}
          </StatusBadge>
        );
      }
      if (
        normalized === "expired" ||
        normalized === "failed" ||
        normalized === "canceled"
      ) {
        return (
          <StatusBadge $color="#a1152d" $background="#ffe3e8">
            {value}
          </StatusBadge>
        );
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    if (type === "delivery") {
      if (normalized === "on_progress") {
        return (
          <StatusBadge $color="#155d9b" $background="#e4f2ff">
            {t.orderDetail.onProgress}
          </StatusBadge>
        );
      }
      if (normalized === "finished") {
        return (
          <StatusBadge $color="#0f7a39" $background="#dff7e8">
            {t.orderDetail.finished}
          </StatusBadge>
        );
      }
      if (normalized === "pending_payment") {
        return (
          <StatusBadge $color="#9a6a00" $background="#fff4da">
            {t.orderDetail.pendingPayment}
          </StatusBadge>
        );
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    return <StatusBadge>{value || "-"}</StatusBadge>;
  };

  return (
    <Container>
      <PageHeader
        title={order?.order_number || ""}
        backPath={`/hebe/${building_code}/hgs/orders`}
        backLabel={t.header.backToOrders}
        showGuestInfo={false}
        buildingCode={building_code}
      />
      <Wrapper>
        {!order ? (
          <Card>{t.order.loadingOrder}</Card>
        ) : (
          <>
            <Card>
              <SectionTitle>{order.order_number}</SectionTitle>
              {order.invoice_number ? (
                <InvoiceBox>
                  <InvoiceLabel>{t.orderDetail.invoiceNumber}</InvoiceLabel>
                  <InvoiceValue>{order.invoice_number}</InvoiceValue>
                </InvoiceBox>
              ) : null}
              <StatusRow>
                <StatusLabel>{t.orderDetail.paymentStatus}</StatusLabel>
                {renderStatusBadge("payment", order.payment_status)}
              </StatusRow>
              <StatusRow>
                <StatusLabel>{t.orderDetail.deliveryStatus}</StatusLabel>
                {renderStatusBadge("delivery", order.delivery_status)}
              </StatusRow>
              <TotalRow>
                <TotalLabel>{t.orderDetail.totalAmount}</TotalLabel>
                <TotalValue>{order.total}</TotalValue>
              </TotalRow>
              {order.created_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.orderedAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.created_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.paid_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.paidAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.paid_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.finished_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.finishedAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.finished_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.payment_link_url ? (
                <ActionRow>
                  <Button
                    href={order.payment_link_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t.orderDetail.openPaymentLink}
                  </Button>
                </ActionRow>
              ) : null}
            </Card>

            <Card>
              <BodySectionTitle>{t.orderDetail.orderItems}</BodySectionTitle>
              <List>
                {(order.lines || []).map((line) => (
                  <ListItem key={line.id}>
                    <ItemHeadline>
                      {line.name} x {line.quantity}
                    </ItemHeadline>
                    {line.note ? (
                      <ItemNote>
                        {t.orderDetail.notePrefix}: {line.note}
                      </ItemNote>
                    ) : null}
                  </ListItem>
                ))}
              </List>
            </Card>

            <Card>
              <BodySectionTitle>{t.orderDetail.notifications}</BodySectionTitle>
              {(order.notifications || []).length === 0 ? (
                <EmptyState>{t.orderDetail.noNotificationsYet}</EmptyState>
              ) : (
                <List>
                  {order.notifications.map((notification) => (
                    <ListItem key={notification.id}>
                      <ItemHeadline>{notification.title}</ItemHeadline>
                      <ItemNote>{notification.message}</ItemNote>
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>

            <OrderMoreRow>
              <OrderMoreLink href={`/hebe/${building_code}/hgs/fnb`}>
                <UtensilsCrossed size={16} />
                {t.orders.orderMoreFnb}
              </OrderMoreLink>
              <OrderMoreLink
                href={`/hebe/${building_code}/hgs/add-on-facility`}
              >
                <BedDouble size={16} />
                {t.orders.orderMoreAddOn}
              </OrderMoreLink>
            </OrderMoreRow>
          </>
        )}
      </Wrapper>
    </Container>
  );
}
